
import React, { useState } from 'react';
import { 
  Card, 
  Upload, 
  Button, 
  Select, 
  Slider, 
  Row, 
  Col, 
  Typography, 
  Space, 
  message,
  Progress,
  Image,
  Divider,
  Input,
  Switch
} from 'antd';
import { 
  UploadOutlined, 
  VideoCameraOutlined, 
  PlayCircleOutlined,
  DownloadOutlined,
  ReloadOutlined,
  PictureOutlined
} from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface VideoParams {
  duration: number;
  fps: number;
  resolution: string;
  motion: string;
  style: string;
  prompt: string;
  loop: boolean;
}

const ImageToVideoPage: React.FC = () => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<string | null>(null);
  const [params, setParams] = useState<VideoParams>({
    duration: 5,
    fps: 24,
    resolution: '1280x720',
    motion: 'smooth',
    style: 'realistic',
    prompt: '',
    loop: false
  });

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    fileList,
    beforeUpload: (file) => {
      const isImage = file.type?.startsWith('image/');
      if (!isImage) {
        message.error('只能上传图片文件！');
        return false;
      }
      const isLt20M = file.size / 1024 / 1024 < 20;
      if (!isLt20M) {
        message.error('图片大小不能超过 20MB！');
        return false;
      }
      return false; // 阻止自动上传
    },
    onChange: (info) => {
      setFileList(info.fileList.slice(-1)); // 只保留最后一个文件
      setResult(null); // 清除之前的结果
    },
    onRemove: () => {
      setFileList([]);
      setResult(null);
    }
  };

  const handleGenerate = async () => {
    if (fileList.length === 0) {
      message.error('请先上传图片！');
      return;
    }

    setProcessing(true);
    setProgress(0);

    try {
      // 模拟处理进度
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + Math.random() * 10;
        });
      }, 800);

      // 这里应该调用实际的API
      // const formData = new FormData();
      // formData.append('file', fileList[0].originFileObj!);
      // formData.append('params', JSON.stringify(params));
      // const response = await imageToVideoApi.generate(formData);

      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 8000));
      
      clearInterval(progressInterval);
      setProgress(100);
      
      // 模拟结果
      setResult('https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4');
      message.success('视频生成完成！');
    } catch (error) {
      console.error('视频生成失败:', error);
      message.error('视频生成失败，请重试！');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = () => {
    if (result) {
      const link = document.createElement('a');
      link.href = result;
      link.download = `generated_video_${Date.now()}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success('下载开始！');
    }
  };

  const handleReset = () => {
    setFileList([]);
    setResult(null);
    setProgress(0);
    setProcessing(false);
  };

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
          <VideoCameraOutlined style={{ marginRight: '8px' }} />
          图片生视频
        </Title>
        <Text type="secondary">将静态图片转换为动态视频，添加运动效果和转场动画</Text>
      </div>

      <Row gutter={[24, 24]}>
        {/* 左侧参数设置 */}
        <Col xs={24} lg={10}>
          <Card title={
            <Space>
              <PictureOutlined />
              图片上传与参数设置
            </Space>
          }>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              {/* 文件上传 */}
              <div>
                <Text strong>选择图片</Text>
                <Upload.Dragger {...uploadProps} style={{ marginTop: '8px' }}>
                  <p className="ant-upload-drag-icon">
                    <UploadOutlined />
                  </p>
                  <p className="ant-upload-text">点击或拖拽图片到此区域上传</p>
                  <p className="ant-upload-hint">
                    支持 JPG、PNG、WEBP 格式，文件大小不超过 20MB
                  </p>
                </Upload.Dragger>
              </div>

              <Divider />

              {/* 视频时长 */}
              <div>
                <Text strong>视频时长: {params.duration}秒</Text>
                <Slider
                  min={1}
                  max={30}
                  step={1}
                  value={params.duration}
                  onChange={(value) => setParams(prev => ({ ...prev, duration: value }))}
                  marks={{
                    1: '1s',
                    5: '5s',
                    10: '10s',
                    20: '20s',
                    30: '30s'
                  }}
                  style={{ marginTop: '8px' }}
                />
              </div>

              {/* 帧率 */}
              <div>
                <Text strong>帧率</Text>
                <Select
                  value={params.fps}
                  onChange={(value) => setParams(prev => ({ ...prev, fps: value }))}
                  style={{ width: '100%', marginTop: '8px' }}
                >
                  <Option value={12}>12 FPS (低质量)</Option>
                  <Option value={24}>24 FPS (标准)</Option>
                  <Option value={30}>30 FPS (高质量)</Option>
                  <Option value={60}>60 FPS (超高质量)</Option>
                </Select>
              </div>

              {/* 分辨率 */}
              <div>
                <Text strong>输出分辨率</Text>
                <Select
                  value={params.resolution}
                  onChange={(value) => setParams(prev => ({ ...prev, resolution: value }))}
                  style={{ width: '100%', marginTop: '8px' }}
                >
                  <Option value="854x480">480P (854x480)</Option>
                  <Option value="1280x720">720P (1280x720)</Option>
                  <Option value="1920x1080">1080P (1920x1080)</Option>
                  <Option value="2560x1440">1440P (2560x1440)</Option>
                </Select>
              </div>

              {/* 运动效果 */}
              <div>
                <Text strong>运动效果</Text>
                <Select
                  value={params.motion}
                  onChange={(value) => setParams(prev => ({ ...prev, motion: value }))}
                  style={{ width: '100%', marginTop: '8px' }}
                >
                  <Option value="smooth">平滑运动</Option>
                  <Option value="zoom_in">缩放进入</Option>
                  <Option value="zoom_out">缩放退出</Option>
                  <Option value="pan_left">左移</Option>
                  <Option value="pan_right">右移</Option>
                  <Option value="rotate">旋转</Option>
                  <Option value="parallax">视差效果</Option>
                </Select>
              </div>

              {/* 风格 */}
              <div>
                <Text strong>视频风格</Text>
                <Select
                  value={params.style}
                  onChange={(value) => setParams(prev => ({ ...prev, style: value }))}
                  style={{ width: '100%', marginTop: '8px' }}
                >
                  <Option value="realistic">真实风格</Option>
                  <Option value="cinematic">电影风格</Option>
                  <Option value="artistic">艺术风格</Option>
                  <Option value="vintage">复古风格</Option>
                  <Option value="modern">现代风格</Option>
                </Select>
              </div>

              {/* 提示词 */}
              <div>
                <Text strong>运动描述 (可选)</Text>
                <TextArea
                  value={params.prompt}
                  onChange={(e) => setParams(prev => ({ ...prev, prompt: e.target.value }))}
                  placeholder="描述您希望的运动效果，例如：轻柔的风吹动头发，背景缓慢移动..."
                  rows={3}
                  style={{ marginTop: '8px' }}
                />
              </div>

              {/* 循环播放 */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text strong>循环播放</Text>
                <Switch
                  checked={params.loop}
                  onChange={(checked) => setParams(prev => ({ ...prev, loop: checked }))}
                />
              </div>

              {/* 操作按钮 */}
              <Space style={{ width: '100%', justifyContent: 'center' }}>
                <Button
                  type="primary"
                  size="large"
                  icon={<PlayCircleOutlined />}
                  onClick={handleGenerate}
                  loading={processing}
                  disabled={fileList.length === 0}
                >
                  生成视频
                </Button>
                <Button
                  size="large"
                  icon={<ReloadOutlined />}
                  onClick={handleReset}
                  disabled={processing}
                >
                  重置
                </Button>
              </Space>
            </Space>
          </Card>
        </Col>

        {/* 右侧预览和结果 */}
        <Col xs={24} lg={14}>
          <Card title={
            <Space>
              <VideoCameraOutlined />
              预览与结果
            </Space>
          }>
            {processing && (
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <Progress 
                  type="circle" 
                  percent={Math.round(progress)} 
                  status={progress === 100 ? 'success' : 'active'}
                />
                <div style={{ marginTop: '16px' }}>
                  <Text>正在生成视频，请稍候...</Text>
                  <br />
                  <Text type="secondary">预计需要 {params.duration * 2} 秒</Text>
                </div>
              </div>
            )}

            <Row gutter={[16, 16]}>
              {/* 原图预览 */}
              {fileList.length > 0 && (
                <Col xs={24} md={result ? 12 : 24}>
                  <Card size="small" title="原图" style={{ textAlign: 'center' }}>
                    <Image
                      src={URL.createObjectURL(fileList[0].originFileObj!)}
                      alt="原图"
                      style={{ maxWidth: '100%', maxHeight: '300px' }}
                      preview={{
                        mask: '点击预览'
                      }}
                    />
                    <div style={{ marginTop: '8px' }}>
                      <Text type="secondary">
                        {fileList[0].name}
                      </Text>
                    </div>
                  </Card>
                </Col>
              )}

              {/* 视频结果 */}
              {result && (
                <Col xs={24} md={12}>
                  <Card 
                    size="small" 
                    title="生成结果" 
                    style={{ textAlign: 'center' }}
                    extra={
                      <Button
                        type="primary"
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={handleDownload}
                      >
                        下载
                      </Button>
                    }
                  >
                    <video
                      src={result}
                      controls
                      style={{ maxWidth: '100%', maxHeight: '300px' }}
                      loop={params.loop}
                    />
                    <div style={{ marginTop: '8px' }}>
                      <Text type="secondary">
                        {params.duration}秒 | {params.fps}FPS | {params.resolution}
                      </Text>
                    </div>
                  </Card>
                </Col>
              )}
            </Row>

            {!fileList.length && !processing && !result && (
              <div style={{ 
                textAlign: 'center', 
                padding: '60px 20px',
                color: '#999'
              }}>
                <VideoCameraOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                <div>请先上传图片开始生成视频</div>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ImageToVideoPage;